import React, { useMemo, useState } from 'react';
import { Alert } from 'reactstrap';

import { useToast } from '~libs/toast';

import { Button, Toolbar } from '~components';
import Icon, { faExclamationTriangle, faPlus } from '~components/icon';
import { Row, TableView } from '~components/table-view-v3';

import { SahService } from '~lookups/models';

import columnsFactory from '~support-at-home/forms/excluded-service-types/list';

import { useGetSahServicesQuery } from '~lookups/actions/sahServices';
import { useGetSettingsQuery } from '~lookups/actions/settings';
import {
  useCreateCompanySettingMutation,
  useGetCompanySettingsQuery,
  useUpdateCompanySettingMutation,
} from '~main/actions/companySettings';

import ExcludeServiceTypeModal from './ExcludeServiceTypeModal';

interface ComponentProps {
  isActive: boolean;
}

const SETTING_KEY = 'sah_service_exclusions';

const ExcludedServiceTypes: React.FC<ComponentProps> = ({ isActive }) => {
  const { showToast } = useToast();
  const [isModalOpen, setModalOpen] = useState(false);

  const { data: lookupSettings = [] } = useGetSettingsQuery({ feature_tag: 'sah' }, { skip: !isActive });
  const { data: companySettings = [] } = useGetCompanySettingsQuery(undefined, { skip: !isActive });
  const { data: sahServices = [] } = useGetSahServicesQuery({}, { skip: !isActive });

  const [createSetting, { isLoading: isCreating }] = useCreateCompanySettingMutation();
  const [updateSetting, { isLoading: isUpdating }] = useUpdateCompanySettingMutation();

  const companySetting = useMemo(() => companySettings.find((s) => s.setting?.key === SETTING_KEY), [companySettings]);

  const excludedIds: string[] = useMemo(() => {
    if (!companySetting?.value) return [];
    try {
      const parsed = JSON.parse(companySetting.value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [companySetting]);

  const excludedServices = useMemo(() => {
    return sahServices.filter((s: SahService) => excludedIds.includes(s.id));
  }, [sahServices, excludedIds]);

  const saveExclusions = async (ids: string[]) => {
    const value = JSON.stringify(ids);
    if (!companySetting) {
      const lookupSettingId = lookupSettings.find((s) => s.key === SETTING_KEY)?.id;
      if (!lookupSettingId) {
        showToast('danger', 'Unable to save: the setting for service type exclusions was not found.');
        return;
      }
      await createSetting({ lookup_setting_id: lookupSettingId, value });
    } else {
      await updateSetting({ id: companySetting.id, value });
    }
  };

  const handleExclude = async (newIds: string[]) => {
    const merged = [...new Set([...excludedIds, ...newIds])];
    await saveExclusions(merged);
    setModalOpen(false);
  };

  const handleRemove = async (row: Row) => {
    const updated = excludedIds.filter((id) => id !== row.id);
    await saveExclusions(updated);
  };

  const columns = columnsFactory(handleRemove);

  return (
    <div className="p-2">
      <Toolbar
        id="excluded-service-types"
        rightCluster={
          <Button id="sah-exclude-service-type-button" size="sm" color="success" onClick={() => setModalOpen(true)}>
            <Icon className="me-2" icon={faPlus} />
            Exclude Service Type
          </Button>
        }
      />

      <Alert color="warning" className="d-flex align-items-start gap-2 mt-2">
        <Icon icon={faExclamationTriangle} className="mt-1" />
        <div>
          These service types are excluded from all SaH features including Claims, Budgets, Supplier Invoices, Price
          Plans, and Appointment Imports.
        </div>
      </Alert>

      <TableView
        id="excluded-service-types"
        columns={columns}
        data={excludedServices as Row[]}
        readonly
        striped
        emptyMessage="No service types are currently excluded"
      />

      <ExcludeServiceTypeModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleExclude}
        currentExclusions={excludedIds}
        isSaving={isCreating || isUpdating}
      />
    </div>
  );
};

export default ExcludedServiceTypes;
